import { useMemo, useState, type MouseEvent } from 'react'
import Icon from '../Icon'
import { api, go, useContent } from '../../lib/classroom'
import { L, w } from '../../lib/words'
import { buzz, celebrate, floatText, resultKick, sound, tapKick } from '../../lib/fun'
import { TAPS, pick, type Lesson, type Step } from '../../../shared/classroom'
import { Rich } from './Step'
import Pichi from './Pichi'

type Q = Extract<Step, { type: 'question' }>

/**
 * PRACTICE — a quiz, not a re-read of the lesson.
 *
 * Only the questions, shuffled, with their choices shuffled too, so a student
 * learns the idea rather than "it was the second button". A wrong pick is
 * crossed out and they try again — the answer is never handed over. A hint
 * (the teaching slide the question came from) is there if they want it; a
 * guess question says "higher" or "lower". The explanation appears once they
 * find it themselves. The server records the FIRST try of each question, and
 * only that one earns XP, so re-tapping can't farm it.
 */
export default function Study({ lessonId, onXp }: { lessonId: string; onXp: () => void }) {
  const modules = useContent()
  const lesson = useMemo(() => {
    for (const m of modules ?? []) for (const l of m.lessons) if (l.id === lessonId) return l
    return null
  }, [modules, lessonId])
  const [round, setRound] = useState(0)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `round` re-shuffles on purpose
  const quiz = useMemo(() => (lesson ? buildQuiz(lesson) : []), [lesson, round])
  const [i, setI] = useState(-1)
  const [firstTry, setFirstTry] = useState<Record<string, boolean>>({})
  const [earned, setEarned] = useState(0)

  if (!modules) return <div className="boot">…</div>
  if (!lesson) return <p className="card">?</p>


  if (i < 0) {
    return (
      <div className="study">
        <section className="card study-intro">
          <Pichi size={110} stage={2} mood="wave" />
          <span className="lesson-num big">{lesson.id}</span>
          <h1>{L(lesson.title)}</h1>
          <h3>{w('topics')}</h3>
          <ul>{lesson.objectives.map((o, k) => <li key={k}>{L(o)}</li>)}</ul>
          <p className="muted small">{quiz.length} {w('questions')} · {w('practiceNote')}</p>
          <button className="primary big" onClick={() => setI(0)}><Icon name="play" /> {w('letsPractice')}</button>
        </section>
      </div>
    )
  }

  if (i >= quiz.length) {
    const right = Object.values(firstTry).filter(Boolean).length
    const pct = quiz.length ? right / quiz.length : 0
    const stars = pct >= 0.9 ? 3 : pct >= 0.6 ? 2 : 1
    return (
      <div className="study done-screen">
        <Pichi size={140} stage={3} mood="cheer" />
        <h1>{w('practiceDone')}</h1>
        <div className="stars" aria-label={`${stars}/3`}>
          {[1, 2, 3].map((n) => <Icon key={n} name="star" size={48} className={n <= stars ? 'on' : ''} />)}
        </div>
        <p className="big-xp">{right}/{quiz.length} {w('firstTry')} · <strong>+{earned} XP</strong></p>
        <section className="card">
          <h3>{w('homework')}</h3>
          <Rich text={L(lesson.homework)} />
        </section>
        <button className="primary big" onClick={() => { setFirstTry({}); setEarned(0); setRound((r) => r + 1); setI(0) }}><Icon name="refresh" /> {w('practiceAgain')}</button>
        <button className="big home-btn" onClick={() => go('/')}><Icon name="home" /> {w('home')}</button>
      </div>
    )
  }

  const item = quiz[i]!
  return (
    <div className="study">
      <header className="live-top">
        <span className="small">{lesson.id} · {w('question')} {i + 1}/{quiz.length}</span>
        <span className="live-chips">
          <span className="chip chip-oro"><Icon name="coin" size={16} /> {earned}</span>
          <button className="quit" onClick={() => go('/')} aria-label={w('quit')}><Icon name="close" size={18} /> {w('quit')}</button>
        </span>
      </header>
      <div className="progress"><div style={{ width: `${((i + 1) / quiz.length) * 100}%` }} /></div>
      <QuizCard
        key={`${round}:${item.q.id}`}
        lesson={lesson}
        item={item}
        onFirst={(ok, xp) => {
          setFirstTry((m) => ({ ...m, [item.q.id]: ok }))
          setEarned((e) => e + xp)
          if (xp) onXp()
        }}
        onNext={() => {
          if (i + 1 >= quiz.length) { celebrate(); sound('level') }
          setI(i + 1)
        }}
      />
    </div>
  )
}

interface Item { q: Q; order: number[]; hint: Extract<Step, { type: 'slide' }> | null }

function shuffle<T>(a: T[]): T[] {
  const b = [...a]
  for (let k = b.length - 1; k > 0; k--) {
    const j = Math.floor(Math.random() * (k + 1))
    ;[b[k], b[j]] = [b[j] as T, b[k] as T]
  }
  return b
}

function buildQuiz(lesson: Lesson): Item[] {
  const items: Item[] = []
  lesson.steps.forEach((s, idx) => {
    if (s.type !== 'question') return
    // The hint is the teaching slide this question follows.
    let hint: Item['hint'] = null
    for (let k = idx - 1; k >= 0; k--) {
      const p = lesson.steps[k]!
      if (p.type === 'slide') { hint = p; break }
    }
    const order = s.kind === 'choice' ? shuffle(s.choices.map((_, n) => n)) : []
    items.push({ q: s, order, hint })
  })
  return shuffle(items)
}

const TONES = ['c1', 'c4', 'c5', 'c3', 'c0', 'c2']
const NOPE = [
  { es: 'Esa no. ¡Prueba otra!', en: 'Not that one. Try another!' },
  { es: 'Casi — piénsalo otra vez.', en: 'Almost — think again.' },
  { es: 'Nop. ¡Tú puedes!', en: 'Nope. You got this!' },
]

function QuizCard({ lesson, item, onFirst, onNext }: {
  lesson: Lesson; item: Item
  onFirst: (right: boolean, xp: number) => void
  onNext: () => void
}) {
  const { q } = item
  const [tries, setTries] = useState(0)
  const [out, setOut] = useState<(number | boolean)[]>([])
  const [solved, setSolved] = useState(false)
  const [nudge, setNudge] = useState<string | null>(null)
  const [showHint, setShowHint] = useState(false)
  const [guess, setGuess] = useState('')
  const [shake, setShake] = useState<number | boolean | 'guess' | null>(null)

  const isRight = (value: number | boolean) => q.kind === 'guess'
    ? Math.abs((value as number) - q.answer) / Math.max(Math.abs(q.answer), 1) <= (q.tolerance ?? 0.2) / 2
    : value === q.answer

  const attempt = async (value: number | boolean | null, e?: MouseEvent) => {
    tapKick(e, L(pick(TAPS)))
    const first = tries === 0
    setTries((t) => t + 1)
    const ok = value !== null && isRight(value)
    if (first) {
      // The first try is the one on the record, graded by the server.
      const res = await api.practice(lesson.id, q.id, value === null ? { idk: true } : { value })
        .catch(() => ({ result: ok ? 'right' as const : 'wrong' as const, xp: 0 }))
      onFirst(res.result === 'right', res.xp)
      if (ok) resultKick('right', res.xp)
      else if (res.xp) floatText(`+${res.xp} XP`, { x: innerWidth / 2, y: innerHeight / 3 })
    } else if (ok) {
      resultKick('right', 0)
    }
    if (value === null) { setShowHint(true); setNudge(w('hintHere')); return }
    if (ok) { setSolved(true); return }
    sound('wrong')
    buzz(30)
    setShake(q.kind === 'guess' ? 'guess' : value)
    setTimeout(() => setShake(null), 450)
    if (q.kind === 'guess') {
      const off = Math.abs((value as number) - q.answer) / Math.max(Math.abs(q.answer), 1)
      setNudge(`${(value as number) < q.answer ? w('higher') : w('lower')}${off <= (q.tolerance ?? 0.2) * 1.5 ? ` — ${w('warm')}` : ''}`)
    } else {
      setOut((o) => [...o, value])
      setNudge(L(pick(NOPE)))
    }
  }

  const n = Number(guess.replace(/[^0-9.-]/g, ''))

  return (
    <div className="quiz">
      <div className="step step-question">
        <h2 className="prompt">{L(q.prompt)}</h2>
      </div>

      {!solved && (
        <div className="answer">
          {q.kind === 'yesno' && (
            <div className="pad pad-2">
              {[true, false].map((v) => (
                <button key={String(v)} className={`choice ${v ? 'c2' : 'c0'}${out.includes(v) ? ' out' : ''}${shake === v ? ' shake' : ''}`}
                  disabled={out.includes(v)} onClick={(e) => void attempt(v, e)}>{v ? w('yes') : w('no')}</button>
              ))}
            </div>
          )}
          {q.kind === 'choice' && (
            <div className="pad">
              {item.order.map((orig, k) => (
                <button key={orig} className={`choice ${TONES[k % TONES.length]}${out.includes(orig) ? ' out' : ''}${shake === orig ? ' shake' : ''}`}
                  disabled={out.includes(orig)} onClick={(e) => void attempt(orig, e)}>
                  <span className="choice-letter">{String.fromCharCode(65 + k)}</span>
                  <span>{L(q.choices[orig])}</span>
                </button>
              ))}
            </div>
          )}
          {q.kind === 'guess' && (
            <form className="guess" onSubmit={(e) => e.preventDefault()}>
              <div className="guess-row">
                {q.unit === '$' && <span className="guess-unit">$</span>}
                <input inputMode="decimal" value={guess} onChange={(e) => setGuess(e.target.value)} placeholder="0" aria-label={w('yourGuess')} />
                {q.unit && q.unit !== '$' && <span className="guess-unit">{q.unit}</span>}
              </div>
              <button type="submit" className={`choice c4${shake === 'guess' ? ' shake' : ''}`} disabled={!guess.trim() || !Number.isFinite(n)}
                onClick={(e) => void attempt(n, e)}>{w('send')}</button>
            </form>
          )}
          {nudge && <p className="nudge" role="status">{nudge}</p>}
          <div className="quiz-tools">
            {tries === 0 && <button className="choice idk" onClick={(e) => void attempt(null, e)}><Icon name="question" /> {w('idk')}</button>}
            {item.hint && !showHint && tries > 0 && <button className="choice idk" onClick={() => setShowHint(true)}><Icon name="bulb" /> {w('seeHint')}</button>}
          </div>
          {showHint && item.hint && (
            <div className="hint-card">
              <strong><Icon name="bulb" /> {L(item.hint.title)}</strong>
              <Rich text={L(item.hint.body)} />
            </div>
          )}
        </div>
      )}

      {solved && (
        <div className="feedback fb-right" role="status">
          <Pichi size={92} stage={2} mood="cheer" />
          <div className="fb-head"><span className="fb-cheer">{tries === 1 ? w('firstTryCheer') : w('foundIt')}</span></div>
          <div className="fb-explain"><Rich text={L(q.explain)} /></div>
          <button className="primary big fb-next" onClick={onNext}>{w('tapNext')} <Icon name="chevron" /></button>
        </div>
      )}
    </div>
  )
}
